import { Geolocation } from "@capacitor/geolocation";
import { selectReverseGeocodingCandidate, ReverseGeocodingCandidate } from "@foresting-tomorrow/converters";

// Project import
import {
  googleGeoCodeingGetGeolocaion,
  googleGeocodingReverseGeocode,
} from "lib/googleMaps/geoLocation";
import { GeoLocationAddress, LatLong } from "types/geoLocation";
import { ensureLocationPermission } from "utils/nativeAppUtils";
import { isNative } from "lib/capacitor/capacitor";

// ==============================|| GEOLOCATION SERVICES ||============================== //
// ------------------ geolocation depending on platform ------------------
const DEFAULT_WATCH_OPTIONS_WEB: WatchOptionsWeb = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 0,
};

const DEFAULT_WATCH_OPTIONS_APP: WatchOptionsApp = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 0,
  minimumUpdateInterval: 100,
};

export type Position = {
  coords: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    altitude?: number | null;
    altitudeAccuracy?: number | null;
    heading?: number | null;
    speed?: number | null;
  };
  timestamp: number;
};

export type WatchOptionsWeb = {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
};

export type WatchOptionsApp = WatchOptionsWeb & {
  minimumUpdateInterval?: number; // Minimum time (in ms) between updates. Only for native apps.
};

type PositionCallback = (position: Position) => void;
type ErrorCallback = (error: GeolocationPositionError | any) => void;

let activeWatchId: string | number | null = null;

// Cross-platform position watcher.
export async function startWatchingPosition(
  success: PositionCallback,
  error: ErrorCallback,
  options?: WatchOptionsApp | WatchOptionsWeb,
) {
  // Native app: use Capacitor Geolocation plugin
  if (isNative) {
    let opt = DEFAULT_WATCH_OPTIONS_APP;
    if (options) {
      opt = { ...opt, ...options };
    }
    try {
      const allowed = await ensureLocationPermission();
      if (!allowed) throw new Error("Location permission not granted");
      activeWatchId = await Geolocation.watchPosition(opt, (pos, err) => {
        if (err) error(err);
        else if (pos) success(pos as Position);
      });
    } catch (err) {
      error(err);
    }
  } else {
    // Web: use standard Geolocation API
    let opt = DEFAULT_WATCH_OPTIONS_WEB;
    if (options) {
      opt = { ...opt, ...options };
    }
    try {
      activeWatchId = navigator.geolocation.watchPosition(success, error, opt);
    } catch (err) {
      error(err);
    }
  }
  return activeWatchId;
}

// Stop watching position.
export async function stopWatchingPosition() {
  if (activeWatchId === null) return;

  if (isNative) {
    await Geolocation.clearWatch({ id: activeWatchId as string });
  } else {
    navigator.geolocation.clearWatch(activeWatchId as number);
  }
  activeWatchId = null;
} 

// Get current position once.
export async function getCurrentPosition(
  options: WatchOptionsApp | WatchOptionsWeb = { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 },
): Promise<Position> {
  if (isNative) {
    const allowed = await ensureLocationPermission();
    if (!allowed) throw new Error("Location permission not granted");

    return await Geolocation.getCurrentPosition(options);
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

// ------------------ Address --> Lat/Lng ------------------
type GetGeolocationResult = {
  bounds: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  } | null;
  location: { lat: number; lng: number } | null;
} | null;

export async function getGeolocation(address: GeoLocationAddress): Promise<GetGeolocationResult> {
  const response = await googleGeoCodeingGetGeolocaion(address);
  if (response.data.results && response.data.results.length > 0) {
    const { location, bounds } = response.data.results[0].geometry;
    return { bounds, location };
  }
  return null;
}

// ------------------ Lat/Lng --> Address ------------------
export async function reverseGeocode(latLong: LatLong): Promise<string | null> {
  try {
    const response = await googleGeocodingReverseGeocode(latLong);
    if (response.data && response.data.results && response.data.results.length > 0) {
      const addressComponents = response.data.results[0].address_components;
      for (const component of addressComponents) {
        if (
          component.types.includes("locality") ||
          component.types.includes("administrative_area_level_2")
        ) {
          return component.long_name;
        }
      }
    }
    return null;
  } catch (error) {
    console.error("Error in reverse geocoding:", error);
    return null;
  }
}

/** Returns the display address closest to a map marker. */
export async function reverseGeocodeNearestAddress(
  latLong: LatLong,
  language?: string,
): Promise<string | null> {
  try {
    const response = await googleGeocodingReverseGeocode(latLong, language);
    const results: ReverseGeocodingCandidate[] = response.data?.results || [];
    const result = selectReverseGeocodingCandidate(results, latLong)?.candidate;
    if (!result?.formatted_address) return null;

    // Google uses this placeholder when a nearby road has no name. Keep the
    // postal/city/country information instead of presenting a false road name.
    return result.formatted_address
      .split(",")
      .map((part: string) => part.trim())
      .filter((part: string) => part.toLocaleLowerCase() !== "unnamed road")
      .join(", ");
  } catch (error) {
    console.error("Error reverse geocoding nearest address:", error);
    return null;
  }
}
